import * as React from "react"
import { Interval, type Period } from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgArrowNarrowLeft } from "@digits-shared/components/SVGIcons/line/ArrowNarrowLeft.svg"
import { SvgArrowNarrowRight } from "@digits-shared/components/SVGIcons/line/ArrowNarrowRight.svg"
import dateTimeHelper, { DateFormat } from "@digits-shared/helpers/dateTimeHelper"
import stringHelper from "@digits-shared/helpers/stringHelper"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"
import { type DateRangeIntervalOptions } from "src/shared/components/DateRangeSelector/dateRangeConstants"
import { useDateRangeContext } from "src/shared/components/DateRangeSelector/DateRangeContext"
import { useFirstTransactionTimestamp } from "src/shared/hooks/useFirstTransactionTimestamp"

/*
  STYLES
*/

const RangeContainer = styled.div`
  user-select: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`

const PeriodContainer = styled.div`
  display: grid;
  gap: 8px;
  align-self: stretch;

  // default months
  grid-template-columns: repeat(3, 45px);
  &.quarter {
    grid-template-columns: repeat(4, 35px);
  }
  &.year {
    grid-template-columns: repeat(5, 55px);
  }

  overflow: hidden;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`

const YearSelector = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  svg {
    cursor: pointer;
    width: 16px;
    height: 16px;
    position: absolute;
    ${svgPathStyles(colors.secondary, 2)};
  }
`

const PeriodOption = styled.div`
  position: relative;
  text-align: center;
  color: ${colors.secondary};
  font-size: 14px;
  font-weight: ${fonts.weight.heavy};
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;

  transition:
    background-color 250ms ease,
    color 250ms ease,
    box-shadow 250ms ease;

  &.selected {
    background: ${colors.translucentSecondary10};
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.3;
  }
`

/*
  INTERFACES
*/

interface PeriodOptionProps {
  interval: Interval
  timestamp: number
  disable?: boolean
  disableAfter?: number
  disableBefore?: number
  dateFormat?: DateFormat
}

/*
  COMPONENTS
*/

export const IntervalYearSelector: React.FC = () => {
  const {
    dateRangeState: {
      timeRange: { interval },
    },
  } = useDateRangeContext()
  const now = dayjs().utc()
  const { firstTransactionTimestamp, firstTransactionYear } = useFirstTransactionTimestamp()

  return (
    <RangeContainer>
      <PeriodContainer className={interval.toLocaleLowerCase()}>
        {Array.from({ length: now.year() - firstTransactionYear + 2 }).map((_, index) => {
          const timestamp = dateTimeHelper.addIntervalToDayjs(now, Interval.Year, index * -1).unix()

          return (
            <IntervalPeriodOption
              key={index}
              interval={interval}
              timestamp={timestamp}
              disableBefore={firstTransactionTimestamp}
              dateFormat={DateFormat.Default}
            />
          )
        })}
      </PeriodContainer>
    </RangeContainer>
  )
}

export const IntervalRangeSelector: React.FC = () => (
  <>
    <FromSelector />
    <ToSelector />
  </>
)

const FromSelector: React.FC = () => {
  const {
    dateRangeState: {
      year,
      timeRange: { interval },
    },
  } = useDateRangeContext()
  const previousRange = dayjs()
    .utc()
    .year(year - 1)
  const { firstTransactionTimestamp, firstTransactionYear } = useFirstTransactionTimestamp()

  return (
    <RangeContainer>
      <YearHeader
        year={previousRange.year()}
        hasPrevious={previousRange.year() > firstTransactionYear}
      />
      <PeriodContainer className={interval.toLocaleLowerCase()}>
        {Array.from({ length: getPeriodLength(interval) }).map((_, index) => {
          const timestamp = dateTimeHelper
            .setIntervalValueToDayjs(previousRange, index + 1, interval)
            .unix()
          return (
            <IntervalPeriodOption
              key={index}
              interval={interval}
              timestamp={timestamp}
              disableBefore={firstTransactionTimestamp}
            />
          )
        })}
      </PeriodContainer>
    </RangeContainer>
  )
}

const ToSelector: React.FC = () => {
  const {
    dateRangeState: {
      year,
      timeRange: { interval },
    },
  } = useDateRangeContext()
  const currentRange = dayjs().utc().year(year)
  const todayOrigin = dateTimeHelper.todayIntervalOrigin(interval)
  const todayPeriod = dateTimeHelper.periodFromIntervalOrigin(todayOrigin)
  const { firstTransactionTimestamp } = useFirstTransactionTimestamp()

  return (
    <RangeContainer>
      <YearHeader year={currentRange.year()} hasNext={todayOrigin.year > currentRange.year()} />
      <PeriodContainer className={interval.toLocaleLowerCase()}>
        {Array.from({ length: getPeriodLength(interval) }).map((_, index) => {
          const timestamp = dateTimeHelper
            .setIntervalValueToDayjs(currentRange, index + 1, interval)
            .unix()
          return (
            <IntervalPeriodOption
              key={index}
              interval={interval}
              timestamp={timestamp}
              disableBefore={firstTransactionTimestamp}
              disableAfter={todayPeriod.endedAt}
            />
          )
        })}
      </PeriodContainer>
    </RangeContainer>
  )
}

const IntervalPeriodOption: React.FC<PeriodOptionProps> = ({
  interval,
  timestamp,
  disable,
  disableAfter,
  disableBefore,
  dateFormat = DateFormat.Micro,
}) => {
  const {
    dateRangeState: { timeRange },
  } = useDateRangeContext()
  const periodToBeRendered = React.useMemo(
    () => dateTimeHelper.periodFromTimestamp(timestamp, interval),
    [interval, timestamp]
  )

  const onPeriodChange = useOnPeriodChange()
  const handleIntervalClick = React.useCallback(() => {
    onPeriodChange(periodToBeRendered)
  }, [onPeriodChange, periodToBeRendered])

  const disabled =
    disable ||
    (disableAfter
      ? disableAfter < periodToBeRendered.startedAt
      : disableBefore
        ? disableBefore > periodToBeRendered.endedAt
        : false)

  const selected = !disabled && timeRange.startedAt <= timestamp && timeRange.endedAt >= timestamp
  const className = stringHelper.classNames(selected && "selected", disabled && "disabled")
  return (
    <PeriodOption className={className} onClick={handleIntervalClick}>
      {dateTimeHelper.displayNameFromUnixTimestamp(timestamp, interval, dateFormat)}
    </PeriodOption>
  )
}

const YearHeader: React.FC<{ hasPrevious?: boolean; year: number; hasNext?: boolean }> = ({
  hasPrevious,
  year,
  hasNext,
}) => {
  const { dateRangeDispatch } = useDateRangeContext()
  const onYearChange = React.useCallback(
    (newYear: number) => {
      dateRangeDispatch({ type: "SET_YEAR", year: newYear })
    },
    [dateRangeDispatch]
  )

  return (
    <YearSelector>
      {hasPrevious && (
        <SvgArrowNarrowLeft css="left: -34px" onClick={() => onYearChange(year - 1)} />
      )}
      <PeriodOption>{year}</PeriodOption>
      {hasNext && <SvgArrowNarrowRight css="right: -34px" onClick={() => onYearChange(year + 1)} />}
    </YearSelector>
  )
}

function useOnPeriodChange() {
  const {
    dateRangeState: { timeRange },
    dateRangeDispatch,
    intervals,
  } = useDateRangeContext()

  return React.useCallback(
    (period: Period) => {
      const isRange = intervals[period.interval as keyof DateRangeIntervalOptions] === "range"
      if (isRange) {
        if (timeRange.startedAt === period.startedAt || timeRange.endedAt < period.endedAt) {
          return dateRangeDispatch({ type: "SET_END", value: period.endedAt })
        }
        if (timeRange.endedAt === period.endedAt || timeRange.startedAt >= period.endedAt) {
          return dateRangeDispatch({ type: "SET_START", value: period.startedAt })
        }
        if (timeRange.endedAt > period.endedAt) {
          return dateRangeDispatch({ type: "SET_START", value: period.startedAt })
        }
        if (timeRange.startedAt < period.endedAt) {
          return dateRangeDispatch({ type: "SET_END", value: period.endedAt })
        }
      }

      const origin = dateTimeHelper.intervalOriginFromDayjs(
        dayjs.unix(period.startedAt).utc(),
        period.interval,
        1
      )
      dateRangeDispatch({ type: "SET_INTERVAL_ORIGIN", origin })
    },
    [dateRangeDispatch, intervals, timeRange.endedAt, timeRange.startedAt]
  )
}

function getPeriodLength(interval: Interval) {
  switch (interval) {
    case Interval.Year:
      return 1
    case Interval.Quarter:
      return 4
    case Interval.Month:
      return 12
    default:
      return 0
  }
}
